import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { formatMoney } from '@theme/money-formatting';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener('currency:change', this.#handleCurrencyChange);
    document.addEventListener('shopify:currency-change', this.#handleCurrencyChange);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener('change', this.#handlePolicyChange);
    this.addEventListener('click', this.#handleCheckoutClick, true);
    this.addEventListener('submit', this.#handleCartSubmit, true);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleEditorSelect);
      document.addEventListener('shopify:block:select', this.#handleEditorSelect);
    }

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }

    this.#scheduleDrawerStateUpdate();

    if (window.Shopify?.designMode && this.hasAttribute('data-editor-open')) {
      requestAnimationFrame(() => this.#themeDrawer?.open());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener('currency:change', this.#handleCurrencyChange);
    document.removeEventListener('shopify:currency-change', this.#handleCurrencyChange);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener('change', this.#handlePolicyChange);
    this.removeEventListener('click', this.#handleCheckoutClick, true);
    this.removeEventListener('submit', this.#handleCartSubmit, true);
    document.removeEventListener('shopify:section:select', this.#handleEditorSelect);
    document.removeEventListener('shopify:block:select', this.#handleEditorSelect);
  }

  /** Opens the drawer when its section or one of its blocks is selected in the theme editor. */
  #handleEditorSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.contains(this) || this.contains(event.target)) this.#themeDrawer?.open();
  };

  #scheduleDrawerStateUpdate() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#syncShippingProgress();
        this.#syncPolicyState();
        this.#updateStickyState();
      });
    });
  }

  #handleCurrencyChange = () => {
    this.#scheduleDrawerStateUpdate();
  };

  #syncShippingProgress() {
    for (const progress of this.querySelectorAll('[data-shipping-progress]')) {
      if (!(progress instanceof HTMLElement)) continue;

      const baseGoal = Number(progress.dataset.baseGoal);
      const cartValue = Number(progress.dataset.cartValue);
      const baseCurrency = progress.dataset.baseCurrency?.toUpperCase() || '';
      const activeCurrency = (
        window.Shopify?.currency?.active ||
        progress.dataset.activeCurrency ||
        baseCurrency
      ).toUpperCase();
      const countryCode = progress.dataset.countryCode?.toUpperCase() || '';

      if (!Number.isFinite(baseGoal) || !Number.isFinite(cartValue) || !activeCurrency) continue;

      const countryGoal = this.#findGoalOverride(progress.dataset.countryOverrides, countryCode);
      const currencyGoal = this.#findGoalOverride(progress.dataset.currencyOverrides, activeCurrency);
      const conversionRate = Number(window.Shopify?.currency?.rate);
      const needsConversion = progress.dataset.autoConvert === 'true' && activeCurrency !== baseCurrency;
      let goal = baseGoal;
      let goalSource = 'base';

      if (countryGoal !== null) {
        goal = countryGoal;
        goalSource = 'country';
      } else if (currencyGoal !== null) {
        goal = currencyGoal;
        goalSource = 'currency';
      } else if (needsConversion && Number.isFinite(conversionRate)) {
        goal = baseGoal * conversionRate;
        goalSource = 'converted';
      } else if (needsConversion) {
        progress.dataset.goalSource = 'conversion-unavailable';
        continue;
      }

      if (progress.dataset.conversionRounding === 'nearest_whole') {
        goal = Math.round(goal);
      } else if (progress.dataset.conversionRounding === 'round_up_whole') {
        goal = Math.ceil(goal);
      }

      const currencyPrecision = this.#getCurrencyPrecision(activeCurrency);
      const goalMinorUnits = Math.max(0, Math.round(goal * Math.pow(10, currencyPrecision)));
      const remaining = Math.max(0, goalMinorUnits - cartValue);
      const percent = goalMinorUnits > 0 ? Math.min(100, Math.max(0, (cartValue / goalMinorUnits) * 100)) : 100;
      const moneyFormat = progress.dataset.moneyFormat || '{{amount}} {{currency}}';
      const remainingMoney = formatMoney(remaining, moneyFormat, activeCurrency);
      const messageTemplate =
        remaining === 0 ? progress.dataset.successMessage : progress.dataset.progressMessage || '[amount]';
      const message = remaining === 0 ? messageTemplate || '' : messageTemplate.replace('[amount]', remainingMoney);
      const messageElement = progress.querySelector('[data-shipping-progress-message]');
      const track = progress.querySelector('[data-shipping-progress-track]');
      const fill = progress.querySelector('[data-shipping-progress-fill]');

      if (messageElement instanceof HTMLElement) messageElement.textContent = message;
      if (track instanceof HTMLElement) track.setAttribute('aria-valuenow', String(Math.round(percent)));
      if (fill instanceof HTMLElement) fill.style.width = `${percent}%`;

      progress.dataset.goalSource = goalSource;
      progress.dataset.goalCurrency = activeCurrency;
      progress.dataset.goalMinorUnits = String(goalMinorUnits);
      progress.hidden = false;
    }
  }

  /**
   * Finds an amount in newline-separated rules such as "US,CA: 75" or "EUR: 65".
   * @param {string | undefined} rules
   * @param {string} requestedCode
   * @returns {number | null}
   */
  #findGoalOverride(rules, requestedCode) {
    if (!rules || !requestedCode) return null;

    for (const rule of rules.split(/\r?\n|;/)) {
      const separatorIndex = rule.search(/[:=]/);
      if (separatorIndex < 0) continue;

      const codes = rule
        .slice(0, separatorIndex)
        .split(',')
        .map((code) => code.trim().toUpperCase());
      if (!codes.includes(requestedCode)) continue;

      const amount = Number(rule.slice(separatorIndex + 1).trim());
      if (Number.isFinite(amount) && amount >= 0) return amount;
    }

    return null;
  }

  /** @param {string} currency */
  #getCurrencyPrecision(currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
    } catch {
      return 2;
    }
  }

  /** @returns {HTMLInputElement | null} */
  #getPolicyCheckbox() {
    return this.querySelector('[data-cart-policy]');
  }

  #syncPolicyState() {
    const checkbox = this.#getPolicyCheckbox();
    const checkoutButton = this.querySelector('.cart__checkout-button');
    if (!(checkoutButton instanceof HTMLButtonElement)) return;

    const isRequired = checkbox?.dataset.cartPolicyRequired === 'true';
    const shouldDim = checkbox?.dataset.dimCheckout === 'true';
    const isPending = Boolean(checkbox && isRequired && !checkbox.checked);

    checkoutButton.classList.toggle('cart__checkout-button--policy-pending', isPending && shouldDim);
    checkoutButton.toggleAttribute('data-policy-pending', isPending);
    checkoutButton.removeAttribute('aria-disabled');
  }

  #handlePolicyChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-cart-policy]')) return;

    const warning = this.querySelector('[data-cart-policy-warning]');
    if (event.target.checked) {
      event.target.removeAttribute('aria-invalid');
      if (warning instanceof HTMLElement) warning.hidden = true;
    }
    this.#syncPolicyState();
  };

  #handleCheckoutClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.cart__checkout-button')) return;

    if (this.#validatePolicy()) return;

    event.preventDefault();
    event.stopPropagation();
  };

  #handleCartSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement) || event.target.id !== 'cart-form') return;
    if (this.#validatePolicy()) return;

    event.preventDefault();
    event.stopPropagation();
  };

  #validatePolicy() {
    const checkbox = this.#getPolicyCheckbox();
    if (!checkbox || checkbox.dataset.cartPolicyRequired !== 'true' || checkbox.checked) return true;

    checkbox.setAttribute('aria-invalid', 'true');
    const warning = this.querySelector('[data-cart-policy-warning]');
    if (warning instanceof HTMLElement) warning.hidden = false;
    checkbox.focus();
    return false;
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#scheduleDrawerStateUpdate();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        if (!shouldAutoOpen || detail?.didError) {
          this.#scheduleDrawerStateUpdate();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          this.#scheduleDrawerStateUpdate();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
